import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";

import { useGetCurrentMaintenanceWindowQuery } from "../api/maintenance.api";
import { MaintenanceStatus, ReminderType } from "../maintenance.enums";
import type {
  MaintenanceHistoryEntry,
  MaintenanceWindowSocketPayload,
} from "../types/maintenance.types";

import { SOCKET_KEYS } from "@/app/constants/socket.constant";
import { useSocketEvents } from "@/hooks/useSocketEvents";
import { SocketNamespaces } from "@/interfaces/globals";

export const isActive = (schedule: MaintenanceHistoryEntry | null): boolean => {
  if (!schedule?.startAt) {
    return false;
  }
  if (
    schedule.status !== MaintenanceStatus.SCHEDULED &&
    schedule.status !== MaintenanceStatus.ACTIVE
  ) {
    return false;
  }

  const now = Date.now();
  const startAt = new Date(schedule.startAt).getTime();

  if (now < startAt) {
    return false;
  }
  if (schedule.endAt && now >= new Date(schedule.endAt).getTime()) {
    return false;
  }

  return true;
};

export const shouldNotify = (
  schedule: MaintenanceHistoryEntry | null,
): boolean => {
  if (!schedule || schedule.status !== MaintenanceStatus.SCHEDULED) {
    return false;
  }

  const now = Date.now();
  const startAt = new Date(schedule.startAt).getTime();

  if (now >= startAt) {
    return false;
  }

  if (schedule.reminderType === ReminderType.IMMEDIATE) {
    return true;
  }

  if (schedule.reminderType === ReminderType.CUSTOM && schedule.reminderAt) {
    return now >= new Date(schedule.reminderAt).getTime();
  }

  return false;
};

interface MaintenanceContextValue {
  schedule: MaintenanceHistoryEntry | null;
  isActive: boolean;
  shouldNotify: boolean;
}

const MaintenanceContext = createContext<MaintenanceContextValue | null>(null);

export const MaintenanceProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { data: initialSchedule, refetch } =
    useGetCurrentMaintenanceWindowQuery(undefined);

  const [schedule, setSchedule] = useState<MaintenanceHistoryEntry | null>(
    null,
  );

  // Seed local state from RTK Query cache on mount and when cache is invalidated
  useEffect(() => {
    if (initialSchedule !== undefined) {
      setSchedule(initialSchedule);
    }
  }, [initialSchedule]);

  useSocketEvents<MaintenanceWindowSocketPayload>(SocketNamespaces.BASE, [
    {
      events: SOCKET_KEYS.MAINTENANCE_WINDOW,
      onEvent: useCallback((payload: MaintenanceWindowSocketPayload) => {
        setSchedule(payload.data);
      }, []),
    },
  ]);

  // Trigger a state update when the CUSTOM reminder time is reached so
  // shouldNotify re-evaluates without waiting for a socket event or refetch
  useEffect(() => {
    if (
      !schedule ||
      schedule.status !== MaintenanceStatus.SCHEDULED ||
      schedule.reminderType !== "CUSTOM" ||
      !schedule.reminderAt
    ) {
      return undefined;
    }

    const now = Date.now();
    const reminderAt = new Date(schedule.reminderAt).getTime();
    const startAt = new Date(schedule.startAt).getTime();

    if (now >= reminderAt || now >= startAt) {
      return undefined;
    }

    const timer = setTimeout((): void => {
      setSchedule((prev) =>
        prev ? { ...prev, updatedAt: new Date().toISOString() } : null,
      );
    }, reminderAt - now);

    return (): void => clearTimeout(timer);
  }, [schedule]);

  // Periodically stamp updatedAt so time-dependent conditions (isActive,
  // shouldNotify) are re-evaluated within ~30s even if specific timers are
  // delayed by tab throttling, long delays, or missed socket events
  useEffect(() => {
    if (!schedule) {
      return undefined;
    }

    const interval = setInterval((): void => {
      setSchedule((prev) =>
        prev ? { ...prev, updatedAt: new Date().toISOString() } : null,
      );
    }, 30_000);

    return (): void => clearInterval(interval);
  }, [schedule]);

  // Refetch current window at startAt so the cache is in sync once the
  // window becomes active, even if no socket event fires from the backend
  useEffect(() => {
    if (!schedule || schedule.status !== "SCHEDULED" || !schedule.startAt) {
      return undefined;
    }

    const now = Date.now();
    const startAtTimestamp = new Date(schedule.startAt).getTime();

    if (now >= startAtTimestamp) {
      return undefined;
    }

    const timer = setTimeout((): void => {
      void refetch();
    }, startAtTimestamp - now);

    return (): void => clearTimeout(timer);
  }, [schedule, refetch]);

  const prevActiveRef = useRef(false);

  // Full page reload when the window ends (active → inactive) to ensure
  // all app state (modals, queries) is reset to pre-maintenance state
  useEffect(() => {
    const currentlyActive = isActive(schedule);

    if (prevActiveRef.current && !currentlyActive) {
      window.location.reload();
    }

    prevActiveRef.current = currentlyActive;
  }, [schedule]);

  const value = useMemo<MaintenanceContextValue>(
    () => ({
      schedule,
      isActive: isActive(schedule),
      shouldNotify: shouldNotify(schedule),
    }),
    [schedule],
  );

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = (): MaintenanceContextValue => {
  const ctx = useContext(MaintenanceContext);

  if (!ctx) {
    throw new Error("useMaintenance must be used within MaintenanceProvider");
  }

  return ctx;
};
